/**
 * SUIVI DU PARCOURS MÉNAGE VERS BETTER STACK (DEV-2316, chantier D8).
 * ============================================================================
 *
 * INTENTION : le parcours du formulaire WattWatchers se lit au même endroit que
 * celui du produit principal, la source Better Stack `frontend-events`, sans
 * qu'un ménage anonyme soit jamais compté comme un utilisateur du produit.
 *
 * Il remplace Mixpanel, qu'aucun outil ne lisait. Même mécanisme que front-v6
 * (`front-v6/frontend/src/core/tracking/track.ts`) : un POST HTTP vers la source
 * d'ingestion, avec la même enveloppe. Deux champs distinguent ce front :
 *
 * - `app: "ww"` : l'application qui parle ;
 * - `actor_type: "menage"` : ni `client` ni `homeys_staff`, de sorte que les
 *   outils `product_analytics_*` de mcp-gis, qui filtrent sur `client`, ne le
 *   voient pas.
 *
 * GARANTIE : le suivi ne casse jamais l'écran. Aucune exception ne sort, aucun
 * appel n'est attendu, un envoi qui échoue est abandonné en silence, et un
 * build sans les variables d'ingestion n'envoie simplement rien.
 *
 * Aucune donnée personnelle : l'enveloppe ne porte que les propriétés du
 * parcours (étape, bouton, durée), jamais un nom, une adresse ou un courriel.
 * L'identifiant du dossier CSTB n'y figure PAS en clair : c'est la clé de
 * reprise d'un dossier qui contient des données personnelles. `user_id` en
 * porte un condensé SHA-256 tronqué, stable (même dossier, même valeur) et non
 * réversible, calculé dans le navigateur (cf. `condenserIdentifiant`).
 */
import { App } from "vue"

export type TrackingPlugin = {
    /**
     * Émet un événement du parcours.
     * @param eventName - Nom de l'événement (cf. `TRACKING_EVENTS`)
     * @param params - Propriétés de l'événement
     */
    $track: (eventName: string, params?: Record<string, any>) => void
    /**
     * Rattache les événements suivants au dossier du ménage.
     * @param id - Identifiant du dossier CSTB
     */
    $identify: (id: string) => void
}

/** L'application qui parle, pour la séparer du produit principal. */
export const TRACKING_APP = "ww"
/** Le ménage n'est ni un client ni un membre de l'équipe. */
export const TRACKING_ACTOR_TYPE = "menage"

export type TrackingEnv = "dev" | "staging" | "preprod" | "prod" | "inconnu"

/** Les adresses de la production. Tout le reste se prouve. */
const HOTES_PROD = new Set([
    "wattwatchers.homeys.io",
    "homeys-app-ww-8577fa802eaa.herokuapp.com",
])

/** Au-delà, l'envoi est abandonné sans bloquer. */
const INGEST_TIMEOUT_MS = 3000

/**
 * Déduit l'environnement de l'adresse servie. Une adresse inconnue vaut
 * `inconnu`, jamais `prod`.
 */
export function deduireEnv(
    hostname: string | null | undefined,
    modeDev = false
): TrackingEnv {
    const hote = (hostname ?? "").trim().toLowerCase()
    if (
        modeDev ||
        hote === "localhost" ||
        hote === "127.0.0.1" ||
        hote === "[::1]" ||
        hote.endsWith(".localhost")
    ) {
        return "dev"
    }
    if (hote === "") return "inconnu"
    if (HOTES_PROD.has(hote)) return "prod"
    if (hote.includes("preprod")) return "preprod"
    if (hote.includes("staging")) return "staging"
    return "inconnu"
}

/** Enveloppe de `frontend-events`, même forme que front-v6. */
export interface TrackingEnvelope {
    timestamp: string
    event: string
    event_version: number
    env: TrackingEnv
    app: typeof TRACKING_APP
    user_id: string | null
    actor_type: typeof TRACKING_ACTOR_TYPE
    session_id: string
    company_id: null
    company_type: null
    subscription_plan: null
    role: null
    subscriptions: string[]
    payload: Record<string, unknown>
}

export function construireEnveloppe(args: {
    event: string
    params?: Record<string, unknown>
    userId: string | null
    sessionId: string
    env: TrackingEnv
    timestamp: string
}): TrackingEnvelope {
    return {
        timestamp: args.timestamp,
        event: args.event,
        event_version: 1,
        env: args.env,
        app: TRACKING_APP,
        user_id: args.userId,
        actor_type: TRACKING_ACTOR_TYPE,
        session_id: args.sessionId,
        // Le ménage n'a ni entreprise, ni offre, ni rôle : absent, pas « vide ».
        company_id: null,
        company_type: null,
        subscription_plan: null,
        role: null,
        subscriptions: [],
        payload: { ...(args.params ?? {}) },
    }
}

/** Séparation de domaine du condensé : propre au suivi WW. */
const PREFIXE_CONDENSE = "ww-suivi:"
/** 32 caractères hexadécimaux = 128 bits, largement assez pour distinguer. */
const LONGUEUR_CONDENSE = 32

/**
 * Condensé stable et non réversible de l'identifiant du dossier :
 * SHA-256 hexadécimal de `ww-suivi:<id>`, tronqué à 32 caractères.
 * Rend `null` si le navigateur ne sait pas le calculer : jamais l'identifiant
 * en clair à la place.
 */
export async function condenserIdentifiant(id: string): Promise<string | null> {
    try {
        const subtle = typeof crypto !== "undefined" ? crypto.subtle : undefined
        if (!subtle) return null
        const octets = new TextEncoder().encode(PREFIXE_CONDENSE + id)
        const condense = await subtle.digest("SHA-256", octets)
        return Array.from(new Uint8Array(condense))
            .map((o) => o.toString(16).padStart(2, "0"))
            .join("")
            .slice(0, LONGUEUR_CONDENSE)
    } catch {
        return null
    }
}

function nouvelIdentifiantDeSession(): string {
    try {
        if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
            return crypto.randomUUID()
        }
    } catch {
        // repli ci-dessous
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/**
 * Fabrique le traceur. Les dépendances sont injectées pour être testées sans
 * navigateur ni réseau.
 */
export function creerTracker(options: {
    url: string | undefined
    token: string | undefined
    hostname: string | null | undefined
    modeDev?: boolean
    fetchImpl?: typeof fetch
    now?: () => Date
}): TrackingPlugin {
    const sessionId = nouvelIdentifiantDeSession()
    const env = deduireEnv(options.hostname, options.modeDev ?? false)
    const now = options.now ?? (() => new Date())
    // Le condensé se calcule de façon asynchrone : chaque événement attend
    // celui de l'identification qui le précède, pour garder l'ordre réel.
    let userIdCondense: Promise<string | null> = Promise.resolve(null)

    const $track = (eventName: string, params?: Record<string, any>) => {
        try {
            if (!options.url || !options.token) return
            const fetchImpl =
                options.fetchImpl ??
                (typeof fetch !== "undefined" ? fetch : undefined)
            if (!fetchImpl) return

            // Horodatage de l'ÉMISSION, pas de l'envoi.
            const timestamp = now().toISOString()
            let timeoutId: ReturnType<typeof setTimeout> | null = null

            userIdCondense
                .then((userId) => {
                    const body = JSON.stringify(
                        construireEnveloppe({
                            event: eventName,
                            params,
                            userId,
                            sessionId,
                            env,
                            timestamp,
                        })
                    )
                    const controller =
                        typeof AbortController !== "undefined"
                            ? new AbortController()
                            : null
                    if (controller) {
                        timeoutId = setTimeout(
                            () => controller.abort(),
                            INGEST_TIMEOUT_MS
                        )
                    }
                    return fetchImpl(options.url as string, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${options.token}`,
                        },
                        body,
                        signal: controller?.signal,
                    })
                })
                .catch(() => {
                    // Envoi silencieux : rien ne remonte à l'écran.
                })
                .finally(() => {
                    if (timeoutId !== null) clearTimeout(timeoutId)
                })
        } catch {
            // Le suivi ne doit jamais planter l'écran.
        }
    }

    const $identify = (id: string) => {
        try {
            if (!id) return
            userIdCondense = condenserIdentifiant(id)
            // Conservé de l'ancien plugin : le formulaire reprend le dossier
            // mémorisé sous cette clé au rechargement.
            localStorage.setItem("form_distinct_id", id)
        } catch {
            // localStorage indisponible : sans conséquence pour le suivi.
        }
    }

    return { $track, $identify }
}

export default {
    install: (app: App) => {
        const config = creerTracker({
            url: import.meta.env.VITE_TRACKING_BS_URL as string | undefined,
            token: import.meta.env.VITE_TRACKING_BS_TOKEN as string | undefined,
            hostname:
                typeof window !== "undefined"
                    ? window.location?.hostname
                    : null,
            modeDev: Boolean(import.meta.env.DEV),
        })

        app.config.globalProperties.$tracking = config
        app.provide("tracking", config)
    },
}
